/**
 * Состав контекст-меню строки задачи (DEV-2052) — один на все носители:
 * список, /my, /search, карточка доски. Пункты без меток — метки поедут
 * пакетом с DEV-1501, когда появятся в строке.
 *
 * «Не следить» здесь нет сознательно: строка листинга не знает наблюдателей
 * (их не возят списком ради экономии), и честного тумблера из неё не собрать —
 * отписка живёт в карточке задачи.
 */
import { PRIORITIES } from './api'

import type { MenuItem } from '@/components/ContextMenu.vue'
import type { V1Priority } from '@/api/clients/tracker-issue'

/** Быстрые сроки подменю: покрывают почти все назначения из списка */
export type DueChoice = 'today' | 'tomorrow' | 'week' | 'clear'

const DUE_CHOICES: DueChoice[] = ['today', 'tomorrow', 'week', 'clear']

interface MenuColumn {
  id: string
  name: string
  statusCategory?: string
}

/** Переводчик — снаружи: у модуля нет своего экземпляра i18n */
type Translate = (key: string) => string

export const issueMenuItems = (t: Translate, columns: MenuColumn[]): MenuItem[] => [
  { key: 'self', label: t('menu.assignSelf') },
  // Статус — только там, где известны колонки проекта строки: на /search
  // их нет, и пункт честно пропадает, а не показывает пустое подменю.
  ...(columns.length
    ? [
        {
          key: 'status',
          label: t('menu.status'),
          children: columns.map((column) => ({ key: column.id, label: column.name })),
        },
      ]
    : []),
  {
    key: 'priority',
    label: t('menu.priority'),
    children: PRIORITIES.map((priority) => ({
      key: priority,
      label: t(`issues.priorities.${priority}`),
    })),
  },
  {
    key: 'due',
    label: t('menu.due'),
    children: DUE_CHOICES.map((choice) => ({ key: choice, label: t(`menu.dueChoices.${choice}`) })),
  },
  { key: 'close', label: t('menu.close'), danger: true },
  // Отклонение показывается только там, где известны колонки проекта: без
  // колонки категории «отклонено» пункту некуда вести, и обещать действие,
  // которое ответит отказом, хуже, чем его не показывать.
  ...(columns.some((column) => column.statusCategory === 'STATUS_CATEGORY_REJECTED')
    ? [{ key: 'reject', label: t('menu.reject'), danger: true }]
    : []),
  { key: 'watch', label: t('menu.watch') },
  { key: 'copy-key', label: t('menu.copyKey') },
  { key: 'copy-link', label: t('menu.copyLink') },
  { key: 'open-tab', label: t('menu.openTab') },
]

/** Быстрый срок → момент времени; начало дня в поясе человека, '' — снять */
export const dueFromChoice = (choice: DueChoice, now = new Date()): string => {
  if (choice === 'clear') return ''

  const days = choice === 'today' ? 0 : choice === 'tomorrow' ? 1 : 7
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days)

  return day.toISOString()
}

/** Что за приоритет пришёл из подменю — для типов на стороне обработчика */
export const isPriorityChoice = (value: string): value is V1Priority =>
  (PRIORITIES as string[]).includes(value)
